import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from "styled-components";
import { useSelector } from "react-redux";
import { selectAnchor, selectRowFromDate, selectRowUserId } from "../data/WallPlannerHoverBox.duck";
import { selectTeamDetails, selectWorkGroupDetails } from "../wallPlanner.duck";
import moment from "moment";
import { DATE_FORMAT } from '@shared/utils/DateUtils';
import { naturalSort } from '@shared/utils/ArrayUtils';
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import { BodySmall, BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { getFullOfficeTitleById } from '@shared/utils/OfficeHelper';

export function WallPlannerHoverBox(props: Props) {
  const anchor = useSelector(selectAnchor);
  const userId = useSelector(selectRowUserId);
  const fromDate = useSelector(selectRowFromDate);
  const officeList = useSelector(selectAllOfficeEntities);
  const teamDetails = useSelector(selectTeamDetails);
  const workGroupDetails = useSelector(selectWorkGroupDetails);

  const userInfo = useMemo(() => {
    if (userId && fromDate && teamDetails) {
      const flattendDetails = [...Object.values(teamDetails).flat(), Object.values(workGroupDetails).flat()].flat().map((fd: any) => fd.whereabouts).flat()
      const info: any = flattendDetails.find((td: any) => td.userInfo.userId === userId);
      return info.userInfo;
    }
  }, [fromDate, teamDetails, userId, workGroupDetails]);

  const userData = useMemo(() => {
    if (userId && fromDate && teamDetails) {
      const flattendDetails = [...Object.values(teamDetails).flat(), Object.values(workGroupDetails).flat()].flat().map((fd: any) => fd.whereabouts).flat()
      const info: any = flattendDetails.find((td: any) => td.userInfo.userId === userId);
      if (info) {
        const startOfWeek = moment(fromDate).clone().startOf('isoWeek').format(DATE_FORMAT);
        const endOfWeek = moment(fromDate).clone().startOf('isoWeek').add(7, 'days').format(DATE_FORMAT);

        // Blocks conjoin consecutive days, so they can start before or run past the hovered week.
        // Filter the individual days rather than the blocks they belong to.
        const days = info.working
          .map((w: any) => w.collection ?? [])
          .flat()
          .filter((day: any) => day.fromDate >= startOfWeek && day.fromDate < endOfWeek);

        return naturalSort(days, 'fromDate');
      }
    }

    return [];
  }, [userId, fromDate, teamDetails, workGroupDetails]);

  const boxRef = useRef<HTMLDivElement>(null);
  const [boxSize, setBoxSize] = useState({width: 0, height: 0});

  useLayoutEffect(() => {
    if (boxRef.current) {
      setBoxSize({width: boxRef.current.offsetWidth, height: boxRef.current.offsetHeight});
    }
  }, [userId, fromDate, userData]);

  // Hang the box off the end of the run of whereabouts, and place it below, above, to the right or
  // to the left of that point, taking the first side it fits on. Staying inside the viewport also
  // keeps it from stretching the document, which is what made the page scroll bar appear.
  const placement = useMemo(() => {
    if (typeof window === 'undefined' || !boxSize.height) {
      return {left: anchor.right, top: anchor.bottom};
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const run = {
      top: anchor.top - scrollTop,
      bottom: anchor.bottom - scrollTop,
      left: anchor.left - scrollLeft,
      right: anchor.right - scrollLeft,
    };

    const bounds = {
      top: Math.max(VIEWPORT_MARGIN, dateBarBottom() + ANCHOR_GAP),
      bottom: window.innerHeight - VIEWPORT_MARGIN,
      left: VIEWPORT_MARGIN,
      right: window.innerWidth - VIEWPORT_MARGIN,
    };

    const alignedLeft = clamp(run.right, bounds.left, bounds.right - boxSize.width);
    const alignedTop = clamp(run.top, bounds.top, bounds.bottom - boxSize.height);

    const sides = [
      {top: run.bottom + ANCHOR_GAP, left: alignedLeft},
      {top: run.top - ANCHOR_GAP - boxSize.height, left: alignedLeft},
      {top: alignedTop, left: run.right + ANCHOR_GAP},
      {top: alignedTop, left: run.left - ANCHOR_GAP - boxSize.width},
    ];

    const fits = (side: {top: number, left: number}) =>
      side.top >= bounds.top && side.top + boxSize.height <= bounds.bottom &&
      side.left >= bounds.left && side.left + boxSize.width <= bounds.right;

    const chosen = sides.find(fits) ?? {top: alignedTop, left: alignedLeft};

    return {top: chosen.top + scrollTop, left: chosen.left + scrollLeft};
  }, [anchor, boxSize.height, boxSize.width]);

  return ReactDOM.createPortal(
    <Wrapper ref={boxRef} x={placement.left} y={placement.top}>
      <UserAvatarAndName className={'wallPlanner__userAvatar'}
                         colour={'darkBlue'}
                         firstName={userInfo.firstName}
                         lastName={userInfo.lastName}
                         hideName={true} />
      <BodySmall className={'wallPlanner__username'} colour={Colours.darkGrey}>{userInfo.firstName + ' ' + userInfo.lastName}</BodySmall>

      {userData.map((ud: any, key) => <DayLine key={key} userData={ud} offices={officeList} />)}
    </Wrapper>,
    document.body,
  );
}

function DayLine(props: { userData: any, offices: OfficeEntity[] }) {
  const {userData, offices} = props;

  const officeAmLabel = useMemo(() => {
    if (!!userData.amLocationId) {
      return getFullOfficeTitleById(userData.amLocationId, offices, ', ');
    }
  }, [offices, userData.amLocationId]);

  const dayNote = useMemo(() => {
    return userData.dayNote
  }, [userData]);

  const officePmLabel = useMemo(() => {
    if (!!userData.pmLocationId) {
      return getFullOfficeTitleById(userData.pmLocationId, offices, ', ');
    }
  }, [offices, userData.pmLocationId]);

  // Picked option item or free text for the whereabouts, per half day.
  const amOptionItemLabel = userData.amOptionItemLabel;
  const pmOptionItemLabel = userData.pmOptionItemLabel;
  const optionItemsMatch = amOptionItemLabel === pmOptionItemLabel;

  if (userData.amOption.key === userData.pmOption.key && userData.amLocationId === userData.pmLocationId) {
    return (<DayWrapper>
      <BodyVerySmall weight={600} colour={Colours.blue}>{moment(userData.fromDate).format('ddd DD').toUpperCase()}</BodyVerySmall>
      <BodyVerySmall weight={600} colour={Colours.black}>{userData.amOption.label}</BodyVerySmall>
      {optionItemsMatch ?
        (amOptionItemLabel && <BodyVerySmall>{amOptionItemLabel}</BodyVerySmall>) : <>
          {amOptionItemLabel && <BodyVerySmall>AM: {amOptionItemLabel}</BodyVerySmall>}
          {pmOptionItemLabel && <BodyVerySmall>PM: {pmOptionItemLabel}</BodyVerySmall>}
        </>}
      {officeAmLabel && <BodyVerySmall>{officeAmLabel}</BodyVerySmall>}
      {dayNote && <BodyVerySmall>{dayNote}</BodyVerySmall>}
    </DayWrapper>);
  } else {
    return (<DayWrapper>
      <BodyVerySmall weight={600} colour={Colours.blue}>{moment(userData.fromDate).format('ddd DD').toUpperCase()}</BodyVerySmall>
      <BodyVerySmall weight={600} colour={Colours.black}>AM: {userData.amOption.label}</BodyVerySmall>
      {amOptionItemLabel && <BodyVerySmall>{amOptionItemLabel}</BodyVerySmall>}
      {officeAmLabel && <BodyVerySmall>{officeAmLabel}</BodyVerySmall>}
      <BodyVerySmall style={{marginTop: 8}} weight={600} colour={Colours.black}>PM: {userData.pmOption.label}</BodyVerySmall>
      {pmOptionItemLabel && <BodyVerySmall>{pmOptionItemLabel}</BodyVerySmall>}
      {officePmLabel && <BodyVerySmall>{officePmLabel}</BodyVerySmall>}
      {dayNote && <BodyVerySmall>{dayNote}</BodyVerySmall>}
    </DayWrapper>);
  }
}

interface Props {
}

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 8;

// Ids of the elements the date bar can occupy: the sticky wrapper, and the row itself which pins
// itself to the top of the window once the page is scrolled.
const DATE_BAR_IDS = ['wallPlannerDateBar', 'wallPlannerDatesRow'];

function dateBarBottom() {
  return DATE_BAR_IDS.reduce((lowestEdge, id) => {
    const element = document.getElementById(id);
    if (!element) return lowestEdge;
    return Math.max(lowestEdge, element.getBoundingClientRect().bottom);
  }, 0);
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(value, max));
}

const Wrapper = styled.div<any>`
  position: absolute;
  min-width: 200px;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 0 17px 2px rgba(0, 87, 255, 0.15);
  display: flex;
  flex-direction: column;
  padding: 8px 16px 16px 16px;
  z-index: 999;
  // the box can overlap the cell it describes, so it must never steal the pointer from it
  pointer-events: none;
  
  .wallPlanner__userAvatar {
    margin: 6px auto 0 auto;
  }
  .wallPlanner__username {
    margin: 0 auto 8px auto;
  }
`

const DayWrapper = styled.div`
  margin-bottom: 14px;
`
